import { usePersistedState } from "@/lib/persistedFilters";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  Plus, ExternalLink, Play, MoreHorizontal, Pencil, Trash2, AppWindow, ShieldCheck, ArrowUpDown, Search, X, Crown,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { AppAudiencePicker } from "@/components/AppAudiencePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Iter 110f — Small inline configurator surfaced only when editing the
// Audit Log app tile. Persists the selected `audit_log_db_connection_id`
// straight into the app doc via the standard PUT /api/apps/{aid} — no
// extra endpoint needed. Backend endpoints resolve this per-request so
// there's no client-side connection selection anymore.
function AuditLogAppConfig({ form, setForm }) {
  const [conns, setConns] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit-log/available-connections");
        setConns(data);
      } catch { setConns([]); }
    })();
  }, []);
  const current = form.audit_log_db_connection_id || "";
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2.5 space-y-2" data-testid="app-form-audit-log-config">
      <div>
        <div className="text-sm font-medium text-slate-800">Audit Log · MySQL DB Connection</div>
        <div className="text-xs text-slate-500">
          Selects the MySQL connection this app queries. Register connections under <b>Settings → Platform → DB Connections</b>.
          End users never see this selector — the connection is resolved server-side.
        </div>
      </div>
      <Select value={current || "__none__"}
              onValueChange={(v) => setForm({ ...form, audit_log_db_connection_id: v === "__none__" ? "" : v })}>
        <SelectTrigger data-testid="app-form-audit-log-conn"><SelectValue placeholder="Not configured" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">Not configured</SelectItem>
          {(conns || []).map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-slate-400">· {c.host}/{c.database}</span></SelectItem>
          ))}
        </SelectContent>
      </Select>
      {conns !== null && conns.length === 0 && (
        <p className="text-[11px] text-amber-700">
          No MySQL DB connections registered yet. Add one under Settings → Platform → DB Connections first.
        </p>
      )}
    </div>
  );
}

const EMPTY = {
  name: "", url: "https://", description: "", icon: "AppWindow", icon_url: "",
  preserve_icon_color: false,
  permission_module: "", audiences: ["super"], embed_allowed: true, newtab_only: false, auto_open_new_tab: false, show_on_dashboard: true, show_new_badge: false, show_beta_badge: false, show_roadmap_badge: false, roadmap_stage: "", display_order: 0, status: "active",
  allowed_orgs: [], denied_orgs: [], allowed_ips: [],
  // Iter 106n — Optional OAuth2 client linkage. When set, launch
  // redirects the browser to the client's `start_url` (SP-initiated
  // OIDC). Bypasses any provider-specific REST-API flow.
  oauth_client_id: "",
};

const parseIps = (text) =>
  text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
const ROLES = ["staff", "partner", "customer"];

// Lifecycle stage badges — off / roadmap / on-deck / future.
const ROADMAP_STAGES = [
  { key: "",        label: "Off",     dot: "bg-slate-200", ring: "border-slate-200" },
  { key: "roadmap", label: "Roadmap", dot: "bg-emerald-500", ring: "border-emerald-300" },
  { key: "on_deck", label: "On Deck", dot: "bg-amber-400",  ring: "border-amber-300"  },
  { key: "future",  label: "Future",  dot: "bg-orange-500", ring: "border-orange-300" },
];
const STAGE_COLOR = {
  roadmap: "bg-emerald-600",
  on_deck: "bg-amber-500",
  future:  "bg-orange-500",
};
const resolveStage = (a) => {
  const s = (a?.roadmap_stage || "").toLowerCase();
  if (STAGE_COLOR[s]) return s;
  return a?.show_roadmap_badge ? "roadmap" : "";
};

// Iter 78: New / Beta / Roadmap-stage are mutually exclusive and render
// as a SINGLE badge in the top-right corner of the app icon. Priority
// order: New > Beta > Roadmap-stage.
export const pickBadge = (a) => {
  if (a?.show_new_badge)  return { kind: "new",  label: "New",  bg: "bg-red-600"  };
  if (a?.show_beta_badge) return { kind: "beta", label: "Beta", bg: "bg-blue-600" };
  const s = resolveStage(a);
  if (s) {
    return {
      kind: `stage-${s}`,
      label: ROADMAP_STAGES.find((r) => r.key === s)?.label || s,
      bg:    STAGE_COLOR[s],
    };
  }
  return null;
};


// Iter 108p — Sort control for the Apps directory. Config-driven so
// operators discover the alternatives (recently updated, access scope,
// display order, etc.) at a glance instead of hunting for a hidden menu.
const SORT_OPTIONS = [
  { key: "name_asc",         label: "Name (A → Z)",       hint: "Alphabetical, default" },
  { key: "name_desc",        label: "Name (Z → A)" },
  { key: "display_order",    label: "Display order",       hint: "Uses the numeric field on each app" },
  { key: "recently_updated", label: "Recently updated" },
  { key: "recently_created", label: "Recently created" },
  { key: "status",           label: "Status",              hint: "Active → Beta → Draft → Archived" },
  { key: "access_scope",     label: "Access scope",        hint: "Internal-only → Staff → Partner → Customer" },
];

function SortMenu({ value, onChange, count, total }) {
  const current = SORT_OPTIONS.find((o) => o.key === value) || SORT_OPTIONS[0];
  const showRatio = typeof total === "number" && total !== count;
  return (
    <div className="flex items-center gap-2" data-testid="apps-sort-wrapper">
      <span className="hidden text-[11px] uppercase tracking-wide text-slate-400 sm:inline" data-testid="apps-count-label">
        {showRatio
          ? <>{count} <span className="lowercase">of</span> {total} apps</>
          : <>{count} app{count === 1 ? "" : "s"}</>}
      </span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="apps-sort-trigger" aria-label="Sort apps">
          <span className="flex items-center gap-1.5 text-slate-700"><ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />{current.label}</span>
        </SelectTrigger>
        <SelectContent align="end">
          {SORT_OPTIONS.map((o) => (
            <SelectItem key={o.key} value={o.key} data-testid={`apps-sort-option-${o.key}`}>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-800">{o.label}</span>
                {o.hint && <span className="text-[10.5px] text-slate-400">{o.hint}</span>}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Iter 108q — Live filter box. Debounce is unnecessary because the
// dataset is capped at ~30-100 apps and the filter is client-side
// (no network round-trip). Cmd/Ctrl-K focuses it from anywhere on
// the page so power users don't need to reach for the mouse.
function FilterInput({ value, onChange }) {
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="relative" data-testid="apps-filter-wrapper">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      <Input
        ref={inputRef}
        data-testid="apps-filter-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Filter apps…"
        className="h-8 w-[220px] pl-7 pr-7 text-xs"
        aria-label="Filter apps"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid="apps-filter-clear"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Clear filter"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <span className="pointer-events-none absolute right-1.5 top-1/2 hidden -translate-y-1/2 select-none rounded border border-slate-200 bg-slate-50 px-1 py-0.5 text-[9.5px] font-mono text-slate-400 md:inline-block" data-testid="apps-filter-hotkey"
            style={{ display: value ? "none" : undefined }}>⌘K</span>
    </div>
  );
}


function DynIcon({ name, className }) {
  const C = Icons[name] || AppWindow;
  return <C className={className} />;
}

export default function Apps({ embedded = false }) {
  const { has, user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [orgSearch, setOrgSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [ipsText, setIpsText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Iter 91ak — bump-version dialog state (super_admin only).
  const [bumpTarget, setBumpTarget] = useState(null);
  const [bumpKind, setBumpKind] = useState("patch");
  const [bumpNotes, setBumpNotes] = useState("");
  // Iter 106n — OAuth2 client list for the SSO linkage dropdown.
  const [oauthClients, setOauthClients] = useState([]);
  // Iter 108p — Sort order for the app grid. Default = alphabetical
  // by name (A→Z); persisted per browser via localStorage so operators
  // don't have to reselect their preference each visit.
  const [sortKey, setSortKey] = useState(() => {
    try { return localStorage.getItem("conduit.apps.sortKey") || "name_asc"; }
    catch { return "name_asc"; }
  });
  useEffect(() => {
    try { localStorage.setItem("conduit.apps.sortKey", sortKey); } catch { /* ignore */ }
  }, [sortKey]);

  // Iter 108q — Live filter box. Matches on name, slug, description,
  // and audiences (case-insensitive, all whitespace-separated
  // tokens must appear somewhere in the haystack) so an operator can
  // jump to an app by typing "mark" or "market" or "customer".
  const [appsUrlParams] = useSearchParams();
  const [filterText, setFilterText] = usePersistedState("apps.filter", "", appsUrlParams.get("q"));

  // Iter 110r — Bulk selection state for the Apps grid. Enables the
  // "Super admin only" bulk-lock toolbar so ops can restrict five
  // internal tools in one click instead of opening five editors.
  const [selected, setSelected] = useState(() => new Set());
  const [bulking, setBulking] = useState(false);
  const toggleSelected = (aid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(aid)) n.delete(aid); else n.add(aid);
    return n;
  });
  const clearSelection = () => setSelected(new Set());
  const filteredApps = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return apps;
    const tokens = q.split(/\s+/);
    return apps.filter((a) => {
      const hay = [
        a.name, a.slug, a.description,
        a.icon, a.status,
        a.permission_module,
        ...(a.audiences || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [apps, filterText]);

  const sortedApps = useMemo(() => {
    const arr = [...filteredApps];
    const nameCmp = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    const num = (x, fallback = 0) => Number.isFinite(+x) ? +x : fallback;
    // Audiences are ordered from least-permissive (super admins only) to
    // most-permissive so operators can spot public apps at a glance.
    const roleScore = (a) => {
      const auds = a.audiences || [];
      if (!auds.length) return -1;
      if (auds.includes("customers")) return 2;
      if (auds.some((x) => x !== "staff")) return 1;
      return 0;
    };
    const STATUS_WEIGHT = { active: 0, beta: 1, draft: 2, archived: 3 };
    switch (sortKey) {
      case "name_desc":       arr.sort((a, b) => nameCmp(b, a)); break;
      case "display_order":   arr.sort((a, b) => (num(a.display_order) - num(b.display_order)) || nameCmp(a, b)); break;
      case "recently_updated":arr.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "") || nameCmp(a, b)); break;
      case "recently_created":arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "") || nameCmp(a, b)); break;
      case "status":          arr.sort((a, b) => ((STATUS_WEIGHT[a.status] ?? 9) - (STATUS_WEIGHT[b.status] ?? 9)) || nameCmp(a, b)); break;
      case "access_scope":    arr.sort((a, b) => (roleScore(a) - roleScore(b)) || nameCmp(a, b)); break;
      case "name_asc":
      default:                arr.sort(nameCmp); break;
    }
    return arr;
  }, [filteredApps, sortKey]);

  const load = () => { api.get("/apps?include_inactive=1").then((r) => setApps(r.data)).catch(() => {}); };

  // Iter 110r / 188 — Bulk audience update for the selected apps, one at a
  // time (typically 3-10 apps; parallel would just spam the audit log).
  // `nextRoles` is a list of audience ids, the same thing the per-app
  // picker writes.
  const bulkSetRoles = async (nextRoles, verb) => {
    if (selected.size === 0) return;
    setBulking(true);
    let ok = 0, fail = 0;
    for (const aid of Array.from(selected)) {
      try {
        await api.put(`/apps/${aid}/audiences`, { audiences: nextRoles });
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setBulking(false);
    clearSelection();
    load();
    if (fail === 0) {
      toast.success(`${verb} ${ok} app${ok === 1 ? "" : "s"}.`);
    } else {
      toast.warning(`${verb} ${ok} app${ok === 1 ? "" : "s"}, ${fail} failed. Check the audit log.`);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (has("organizations.view")) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    if (has("users.view")) api.get("/users").then((r) => setAllUsers(r.data)).catch(() => {});
    // Iter 106n — Fetch OAuth2 clients (requires settings.manage).
    // Silently ignore failures for operators without that perm.
    if (has("settings_developer.view")) {
      api.get("/oauth/admin/clients")
        .then((r) => setOauthClients(r.data?.clients || []))
        .catch(() => {});
    }
  }, [has]);

  // Iter 90h — deep link: /apps?edit={app_id} auto-opens the edit dialog
  // for that app once the list has loaded. Used by the Dashboard's
  // super-admin quick-edit pencil.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || !apps.length || dialogOpen) return;
    const target = apps.find((a) => a.app_id === editId);
    if (target) {
      openEdit(target);
      // Strip the query param so refreshes don't loop the dialog open.
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
    // We intentionally omit dialogOpen from deps — we only want this to
    // fire when the URL changes or the apps list first arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, apps]);

  const openLaunch = (app) => {
    if (app.app_type === "native" && app.route) {
      navigate(app.route);
      return;
    }
    const NATIVE_ROUTES = { reports: "/reports" };
    const nativeRoute = NATIVE_ROUTES[(app.key || app.app_key || "").toLowerCase()]
      || NATIVE_ROUTES[(app.name || "").toLowerCase()];
    if (nativeRoute) { navigate(nativeRoute); return; }
    // Iter 110ah — Mirror the `NATIVE_RENDER_SLUGS` short-circuit that
    // Dashboard.launchApp uses. AppViewer renders these slugs inline
    // via a slug switch (StatusPagePanel / AuditLogPage) so they
    // never need a URL — without this we'd throw "no URL configured"
    // whenever an admin flips `embed_allowed` off.
    const NATIVE_RENDER_SLUGS = new Set(["audit-log", "status-page"]);
    if (NATIVE_RENDER_SLUGS.has(app.slug)) {
      navigate(`/apps/${app.slug}`);
      return;
    }
    if (!app.embed_allowed) {
      const url = (app.url || "").trim();
      if (!url || url === "https:" || url === "http:" || !/^https?:\/\/.+/i.test(url)) {
        toast.error(`${app.name} has no URL configured yet — set one in the app editor.`);
        return;
      }
      api.post(`/apps/${app.app_id}/launch`).catch(() => {});
      window.open(url, "_blank");
      toast.success(`Opened ${app.name} in new tab`);
      return;
    }
    navigate(`/apps/${app.slug || app.app_id}`);
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setIpsText(""); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setIpsText((a.allowed_ips || []).join("\n")); setDialogOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, display_order: Number(form.display_order) || 0, allowed_ips: parseIps(ipsText) };
      if (editing) { await api.put(`/apps/${editing.app_id}`, payload); toast.success("App updated"); }
      else { await api.post("/apps", payload); toast.success("App registered"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/apps/${deleteTarget.app_id}`); toast.success("App deleted"); setDeleteTarget(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Iter 91ak — Bump semver + append changelog row (super_admin only, gated
  // server-side too).
  const doBump = async () => {
    if (!bumpTarget) return;
    if (!bumpNotes.trim()) { toast.error("Release notes are required."); return; }
    try {
      const { data } = await api.post(`/apps/${bumpTarget.app_id}/bump-version`,
        { kind: bumpKind, notes: bumpNotes.trim() });
      toast.success(`${bumpTarget.name} bumped to v${data.version}`);
      setBumpTarget(null); setBumpKind("patch"); setBumpNotes("");
      // Refresh the edit dialog + the tile list so the new version shows.
      if (editing?.app_id === bumpTarget.app_id) setEditing({ ...editing, version: data.version });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onIconFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Icon must be under 512 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, icon_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const canManage = has("apps.edit");
  const canCreate = has("apps.create");
  const canDelete = has("apps.delete");

  return (
    <div>
      {embedded ? (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <FilterInput value={filterText} onChange={setFilterText} />
          <SortMenu value={sortKey} onChange={setSortKey} count={sortedApps.length} total={apps.length} />
          {canCreate && (
            <Button data-testid="add-app-button" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> Register app</Button>
          )}
        </div>
      ) : (
        <PageHeader testid="apps-header" title="Apps" subtitle="Embedded application directory and access rules.">
          <FilterInput value={filterText} onChange={setFilterText} />
          <SortMenu value={sortKey} onChange={setSortKey} count={sortedApps.length} total={apps.length} />
          {canCreate && (
            <Button data-testid="add-app-button" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> Register app</Button>
          )}
        </PageHeader>
      )}

      {apps.length === 0 ? (
        <Card className="border-slate-200 shadow-sm p-12 text-center text-slate-400">No apps available to you yet.</Card>
      ) : sortedApps.length === 0 ? (
        <Card className="border-slate-200 shadow-sm p-10 text-center text-slate-500" data-testid="apps-filter-empty">
          <Search className="mx-auto h-8 w-8 text-slate-300" />
          <div className="mt-3 font-heading text-sm font-semibold text-slate-700">No apps match &ldquo;{filterText}&rdquo;</div>
          <p className="mt-1 text-xs text-slate-500">Try a shorter search term or clear the filter to see all {apps.length} apps.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setFilterText("")} data-testid="apps-filter-clear-empty">
            <X className="h-3.5 w-3.5" /> Clear filter
          </Button>
        </Card>
      ) : (
        <>
          {/* Iter 110r — Sticky bulk-action toolbar. Only shown when
              at least one card is selected. Two verbs: "Lock" (write
              `["super_admin"]`) and "Unlock" (write `["staff"]` — the
              historical default). We don't try to restore each app's
              previous roles because we don't cache them server-side;
              ops can always cherry-pick edits from the modal for
              anything more exotic than staff-wide access. */}
          {canManage && selected.size > 0 && (
            <div
              data-testid="apps-bulk-toolbar"
              className="sticky top-2 z-30 mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 shadow-sm"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <Crown className="h-4 w-4 text-amber-600" />
                <span data-testid="apps-bulk-count">
                  {selected.size} app{selected.size === 1 ? "" : "s"} selected
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  size="sm"
                  data-testid="apps-bulk-lock-btn"
                  disabled={bulking}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                  onClick={() => bulkSetRoles(["super"], "Locked")}
                >
                  <Crown className="h-3.5 w-3.5" /> Super admin only
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="apps-bulk-unlock-btn"
                  disabled={bulking}
                  onClick={() => bulkSetRoles(["staff"], "Restored")}
                >
                  Restore staff access
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="apps-bulk-clear-btn"
                  onClick={clearSelection}
                >
                  <X className="h-3.5 w-3.5" /> Clear
                </Button>
              </div>
            </div>
          )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sortedApps.map((a) => (
            <Card key={a.app_id} data-inactive={a.status !== "active" ? "true" : undefined}
              className={`shadow-sm p-5 flex flex-col ${selected.has(a.app_id) ? "ring-2 ring-amber-400 border-amber-300" : "border-slate-200"} ${
                a.status !== "active"
                  ? "bg-slate-100/90 border-slate-300 dark:bg-slate-800/60 dark:border-slate-700 [&_*]:!text-slate-500 dark:[&_*]:!text-slate-400 [&_button]:opacity-70"
                  : ""}`} data-testid={`app-card-${a.app_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2">
                  {canManage && (
                    <Checkbox
                      className="mt-1.5"
                      checked={selected.has(a.app_id)}
                      onCheckedChange={() => toggleSelected(a.app_id)}
                      data-testid={`app-select-${a.app_id}`}
                      aria-label={`Select ${a.name}`}
                    />
                  )}
                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#eef2fb] text-primary">
                    <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl">
                      {a.icon_url ? <img src={a.icon_url} alt={a.name} className="h-full w-full object-cover" /> : <DynIcon name={a.icon} className="h-6 w-6" />}
                    </span>
                    {(() => { const b = pickBadge(a); return b ? <span data-testid={`app-badge-${a.app_id}`} className={`absolute -right-1.5 -top-1.5 z-10 rounded-full ${b.bg} px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white shadow ring-2 ring-white`}>{b.label}</span> : null; })()}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {a.app_type === "native" && <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">Built-in</span>}
                  {a.allowed_ips?.length > 0 && <span className="flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600" data-testid={`app-ip-badge-${a.app_id}`}><ShieldCheck className="h-3 w-3" /> IP</span>}
                  {!a.embed_allowed && a.app_type !== "native" && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">External</span>}
                  {(canManage || canDelete) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`app-actions-${a.app_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canManage && <DropdownMenuItem onClick={() => openEdit(a)}><Pencil className="h-4 w-4" /> Edit</DropdownMenuItem>}
                        {canDelete && <DropdownMenuItem onClick={() => setDeleteTarget(a)} className="text-red-600 focus:text-red-600" data-testid={`app-delete-${a.app_id}`}><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              <h3 className="mt-3 font-heading text-base font-semibold text-slate-900">{a.name}</h3>
              <p className="mt-1 text-sm text-slate-500 flex-1">{a.description}</p>
              <Button data-testid={`launch-app-${a.app_id}`} onClick={() => openLaunch(a)} className="mt-4 w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                {(a.embed_allowed || a.app_type === "native") ? <><Play className="h-4 w-4" /> Launch</> : <><ExternalLink className="h-4 w-4" /> Open</>}
              </Button>
            </Card>
          ))}
        </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit app" : "Register app"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Iter 188 — audiences replace the old super-admin-only switch:
                ops pick who the app is for in plain words, and super admins
                always have access regardless. */}
            <AppAudiencePicker value={form.audiences} appId={editing?.app_id}
              onChange={(audiences) => setForm((f) => ({ ...f, audiences }))} />
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="app-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>URL</Label><Input data-testid="app-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></div>
            <div className="space-y-1.5" data-testid="app-permission-module-row">
              <Label>Permission module <span className="font-normal text-slate-400">(optional)</span></Label>
              <Input data-testid="app-permission-module-input" value={form.permission_module || ""} placeholder={editing?.permission_module || "derived from the name, e.g. my_app"}
                onChange={(e) => setForm({ ...form, permission_module: e.target.value })} className="font-mono text-xs" />
              <p className="text-xs text-slate-400">
                Holding <code className="text-slate-500">{(form.permission_module || editing?.permission_module || "<module>").trim() || "<module>"}.view</code> opens this app. Two apps can share a module. Renaming carries existing grants over.
              </p>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="space-y-1.5">
              <Label>App icon (square image)</Label>
              <div className="flex items-center gap-3">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-[#eef2fb] text-primary">
                  {form.icon_url ? <img src={form.icon_url} alt="icon" className="h-full w-full object-cover" /> : <DynIcon name={form.icon} className="h-7 w-7" />}
                </span>
                <div className="flex-1 space-y-1.5">
                  <Input type="file" accept="image/*" data-testid="app-icon-upload" onChange={onIconFile} className="cursor-pointer text-xs" />
                  {form.icon_url && <button type="button" data-testid="app-icon-remove" onClick={() => setForm({ ...form, icon_url: "" })} className="text-xs text-red-500 hover:underline">Remove image</button>}
                </div>
              </div>
              <p className="text-xs text-slate-400">Square image recommended (PNG/SVG, &lt; 512 KB). Used as the app button on the dashboard.</p>
              {/* Iter 110y — Per-app opt-out for the Icon Normalisation
                  Pass. When OFF (default) uploaded PNGs get a
                  monochrome grayscale filter for uniform dashboard
                  visuals. Flip ON for vendor logos where colour is
                  load-bearing (e.g. Stripe purple, Zoom blue). Only
                  applies to uploaded `icon_url` images; DynIcons
                  always use `text-primary`. */}
              {form.icon_url && (
                <label
                  className="mt-1 flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5"
                  data-testid="app-preserve-icon-color-row"
                >
                  <div className="text-[11px] text-slate-600">
                    Preserve original icon colour
                    <span className="ml-1 text-slate-400">(skip the dashboard monochrome filter)</span>
                  </div>
                  <Switch
                    data-testid="app-preserve-icon-color-switch"
                    checked={!!form.preserve_icon_color}
                    onCheckedChange={(v) => setForm({ ...form, preserve_icon_color: v })}
                  />
                </label>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Fallback icon (lucide name)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="AppWindow" /></div>
              <div className="space-y-1.5"><Label>Display order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} /></div>
            </div>

            {/* Iter 106n — OAuth2 client linkage. When selected, this
                app launches via SP-initiated OIDC (Conduit redirects
                the browser to the client's `start_url`). Leave as
                "None" for apps that use a REST-API SSO (e.g. Quoter,
                internal SPAs) or that embed directly. */}
            {has("settings_developer.view") && (
              <div className="space-y-1.5" data-testid="app-oauth-client-section">
                <Label>OAuth2 client (SSO)</Label>
                <Select value={form.oauth_client_id || "__none__"} onValueChange={(v) => setForm({ ...form, oauth_client_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="app-oauth-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None (no OAuth linkage)</SelectItem>
                    {oauthClients.filter((c) => c.active).map((c) => (
                      <SelectItem key={c.client_id} value={c.client_id}>
                        {c.name} <span className="text-slate-400">— {c.client_id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Launch redirects to the linked client&apos;s <b>Starting endpoint</b>. Configure that URL in <span className="font-mono">Settings → Integrations → OAuth2 Clients Admin</span>.</p>
              </div>
            )}

            {/* Iter 91ak — Version + Changelog. Only shows when editing an
                existing app (bumping requires a persisted app_id). */}
            {editing && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="app-version-section">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Version</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      Current: <span data-testid="app-current-version" className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-slate-800 dark:text-slate-100">v{editing.version || "2.0.0"}</span>
                    </div>
                  </div>
                  {user?.role === "super_admin" && (
                    <Button data-testid="app-bump-version" type="button" size="sm" variant="outline"
                            onClick={() => setBumpTarget(editing)}>
                      Bump version
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400">SemVer is bumped explicitly by super admins. Regular saves don&apos;t touch this.</p>
              </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="app-access-note">
              <div className="text-sm font-medium text-slate-700">Who can see this app?</div>
              <p className="mt-0.5 text-xs text-slate-500">
                Everyone in the audiences ticked above, plus anyone whose role or account grants <code className="text-slate-500">{(form.permission_module || editing?.permission_module || "<module>").trim() || "<module>"}.view</code> in <span className="font-medium text-primary">Settings → Permissions</span>. Whole-organization allow/deny lives on the <span className="font-medium text-primary">Partners</span> &amp; <span className="font-medium text-primary">Customers</span> pages (row → Manage apps). Super admins always have access.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div><div className="text-sm font-medium text-slate-700">Allow iframe embed</div><div className="text-xs text-slate-400">Off = open in new tab only</div></div>
              <Switch checked={form.embed_allowed} onCheckedChange={(v) => setForm({ ...form, embed_allowed: v })} data-testid="app-embed-switch" disabled={!!form.newtab_only} />
            </div>
            {/* Iter 108s — New-tab-only. Overrides embed_allowed and
                replaces the iframe with a light-orange instructional
                card + a single CTA linked to the SSO init URL. Still
                composes with auto_open_new_tab: when both are ON, the
                card renders AND the new tab fires automatically. */}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-700">App works only in a new tab</div>
                <div className="text-xs text-slate-400">
                  When on, the app is <span className="font-semibold">never</span> iframed. Users see a friendly light-orange instruction card with an &ldquo;Open in new tab&rdquo; button linked to the SSO init URL. <span className="font-semibold">Overrides &ldquo;Allow iframe embed&rdquo;</span>. If &ldquo;Auto-open in new tab&rdquo; is also on, the tab opens automatically right after the card is shown.
                </div>
              </div>
              <Switch checked={!!form.newtab_only} onCheckedChange={(v) => setForm({ ...form, newtab_only: v })} data-testid="app-newtab-only-switch" />
            </div>
            {/* Iter 102 — Opt-in auto-open. When embed_allowed is off OR
                a transient SSO failure flips it off at launch time, this
                toggle decides whether to auto-open a new tab or wait
                for the user to click the explicit button. Default off. */}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-700">Auto-open in new tab</div>
                <div className="text-xs text-slate-400">
                  When on, launching this app when it can&apos;t be embedded (or when SSO fails) will immediately open a new browser tab. When off, users see an explicit &ldquo;Open in new tab&rdquo; button instead &mdash; no surprise pop-up. Recommended off for SSO-protected apps.
                </div>
              </div>
              <Switch checked={!!form.auto_open_new_tab} onCheckedChange={(v) => setForm({ ...form, auto_open_new_tab: v })} data-testid="app-auto-open-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div><div className="text-sm font-medium text-slate-700">Show on dashboard</div><div className="text-xs text-slate-400">Off = hidden from the main dashboard app grid (still on the Apps page)</div></div>
              <Switch checked={form.show_on_dashboard !== false} onCheckedChange={(v) => setForm({ ...form, show_on_dashboard: v })} data-testid="app-dashboard-switch" />
            </div>
            {editing?.slug === "audit-log" && <AuditLogAppConfig form={form} setForm={setForm} />}
            <div className="rounded-lg border border-slate-200 px-3 py-2.5 space-y-2.5" data-testid="app-badge-picker">
              <div>
                <div className="text-sm font-medium text-slate-700">Status badge <span className="text-xs font-normal text-slate-400">(only one)</span></div>
                <div className="mt-0.5 text-xs text-slate-400">Renders as a single pill on the top-right of the app icon. New, Beta, and the three Roadmap stages are mutually exclusive.</div>
              </div>
              <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
                {[
                  { key: "off",     label: "None",    dot: "bg-slate-200", ring: "border-slate-300" },
                  { key: "new",     label: "New",     dot: "bg-red-500",   ring: "border-red-300"   },
                  { key: "beta",    label: "Beta",    dot: "bg-blue-500",  ring: "border-blue-300"  },
                  { key: "roadmap", label: "Roadmap", dot: "bg-emerald-500", ring: "border-emerald-300" },
                  { key: "on_deck", label: "On Deck", dot: "bg-amber-400", ring: "border-amber-300" },
                  { key: "future",  label: "Future",  dot: "bg-orange-500", ring: "border-orange-300" },
                ].map((opt) => {
                  const current = form.show_new_badge ? "new"
                    : form.show_beta_badge ? "beta"
                    : (form.roadmap_stage || (form.show_roadmap_badge ? "roadmap" : "off"));
                  const active = current === opt.key;
                  const apply = () => {
                    const stage = ["roadmap", "on_deck", "future"].includes(opt.key) ? opt.key : "";
                    setForm({
                      ...form,
                      show_new_badge:     opt.key === "new",
                      show_beta_badge:    opt.key === "beta",
                      roadmap_stage:      stage,
                      show_roadmap_badge: stage === "roadmap",   // legacy compat
                    });
                  };
                  return (
                    <button key={opt.key} type="button"
                            data-testid={`app-badge-${opt.key}`}
                            onClick={apply}
                            className={`flex items-center justify-center gap-1.5 rounded-full border px-2 py-1.5 text-[11px] font-semibold transition ${
                              active
                                ? `${opt.ring} bg-slate-50 text-slate-800 shadow-sm`
                                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                            }`}>
                      <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5" data-testid="app-ip-allowlist">
              <Label className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-amber-500" /> Restrict by IP address <span className="font-normal text-slate-400">(optional)</span></Label>
              <p className="text-xs text-slate-400">Leave empty to allow any network. Enter one IP or CIDR range per line (e.g. <code className="text-slate-500">203.0.113.5</code> or <code className="text-slate-500">10.0.0.0/24</code>). Launch is blocked from non-matching IPs.{parseIps(ipsText).length > 0 && <span className="ml-1 font-medium text-amber-600">{parseIps(ipsText).length} rule{parseIps(ipsText).length > 1 ? "s" : ""}</span>}</p>
              <Textarea data-testid="app-ips-input" value={ipsText} onChange={(e) => setIpsText(e.target.value)} rows={3} placeholder={"203.0.113.5\n10.0.0.0/24"} className="font-mono text-xs" />
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="app-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-app-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">{editing ? "Save" : "Register"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete app?</AlertDialogTitle>
            <AlertDialogDescription>This removes {deleteTarget?.name} from the directory.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-app" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 91ak — Bump version dialog */}
      <Dialog open={!!bumpTarget} onOpenChange={(o) => !o && setBumpTarget(null)}>
        <DialogContent className="max-w-md" data-testid="bump-version-dialog">
          <DialogHeader>
            <DialogTitle>Bump version — {bumpTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/30 p-3 text-sm">
              <div className="text-slate-500 dark:text-slate-400">Current version</div>
              <div className="mt-0.5 font-mono text-slate-800 dark:text-slate-100">v{bumpTarget?.version || "2.0.0"}</div>
            </div>
            <div className="space-y-1.5">
              <Label>Bump type</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "patch", label: "Patch", hint: "Bug fixes" },
                  { key: "minor", label: "Minor", hint: "New feature" },
                  { key: "major", label: "Major", hint: "Breaking / rewrite" },
                ].map((opt) => (
                  <button key={opt.key}
                          type="button"
                          data-testid={`bump-kind-${opt.key}`}
                          onClick={() => setBumpKind(opt.key)}
                          className={`rounded-md border px-3 py-2 text-left text-xs transition ${
                            bumpKind === opt.key
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300"
                          }`}>
                    <div className="font-semibold">{opt.label}</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400">{opt.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Release notes <span className="text-red-500">*</span></Label>
              <Textarea data-testid="bump-notes"
                        rows={4}
                        value={bumpNotes}
                        onChange={(e) => setBumpNotes(e.target.value)}
                        placeholder="What changed in this release?" />
              <p className="text-xs text-slate-400">Users will see this on the /changelog page and as an in-app notification.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBumpTarget(null)}>Cancel</Button>
            <Button data-testid="bump-save" onClick={doBump} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
